import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { CategoryPickerCategory, CategoryPickerComponent, CategoryPickerSubcategory } from "@me-pos/category-picker";
import i18next from "i18next";
import { useUpdateMenuCategoryMutation } from "@me-team/host/main/menu/graphql/menu.hooks";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";
import { useMenuLanguagesData } from "../utils/hooks/useMenuLanguagesData";
import { buildTranslationTextVm } from "../utils/translationVersions/buildTranslationTextVm";
import TranslationMissingDot from "../components/LanguageVersionSelect/TranslationMissingDot";
import { EditMenuService } from "../../MenuEditContentPage/service/editMenuService";
import { Spinner } from "@services/ui-components";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useParams } from "react-router-dom";

interface MoveCategoryTarget {
    id: number;
    name?: string | null;
    nameOrigin?: string | null;
    parentCategory?: {
        id: number;
    } | null;
    favicon?: {
        id: number;
    } | null;
    deleted: boolean;
}

interface MoveCategoryChildOption {
    id: number;
    name?: string | null;
    nameOrigin?: string | null;
}

interface MoveCategoryOption {
    id: number;
    name?: string | null;
    nameOrigin?: string | null;
    childCategories?: MoveCategoryChildOption[] | null;
}

interface MoveCategoryModalProps {
    show: boolean;
    toggleShowToast: (text: string) => void;
    handleClose: () => void;
    categoryToMove: MoveCategoryTarget;
    categories: MoveCategoryOption[];
    categoriesLoading: boolean;
    onMove: () => void;
    canMoveCategory?: boolean;
}

const MoveCategoryModal: React.FC<MoveCategoryModalProps> = ({
    show,
    handleClose,
    categoryToMove,
    categories,
    categoriesLoading,
    onMove,
    toggleShowToast,
    canMoveCategory,
}) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const selectedLangVersionCode = useReactiveVar(menuLanguageVersionVar);
    const { primaryLanguage } = useMenuLanguagesData(Number(currentCompanyId));
    const primaryLanguageIso = primaryLanguage?.isoCode?.trim() || null;
    const isTranslationMode = Boolean(
        selectedLangVersionCode && primaryLanguageIso && selectedLangVersionCode !== primaryLanguageIso
    );
    const nameVm = buildTranslationTextVm({
        translatedValue: categoryToMove?.name,
        originValue: categoryToMove?.nameOrigin,
        isTranslationMode,
    });

    const [currentCategoryId, setCurrentCategoryId] = useState<number | null>(
        categoryToMove.parentCategory ? categoryToMove.parentCategory.id : categoryToMove.id
    );
    const [currentSubcategoryId, setCurrentSubcategoryId] = useState<number | null>(
        categoryToMove.parentCategory ? categoryToMove.id : null
    );
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(categoryToMove.id);
    const [searchValue, setSearchValue] = useState("");
    const pickerWrapperRef = useRef<HTMLDivElement | null>(null);

    const [updateMenuCategory] = useUpdateMenuCategoryMutation();
    const canMoveCategoryByRole = useRoleAccess(CompanyAccess.MenuDishCategoryEdit);
    const canSubmitMove = canMoveCategory ?? canMoveCategoryByRole;
    const safeCategoryName = EditMenuService.resolveTranslatedName(categoryToMove?.name, categoryToMove?.nameOrigin);
    const primaryCategoryName = categoryToMove?.nameOrigin || categoryToMove?.name || "";
    const pickerCategories = useMemo(
        () =>
            EditMenuService.categoryModuleCreator(
                categories,
                currentCategoryId,
                currentSubcategoryId,
                isTranslationMode
            ),
        [categories, currentCategoryId, currentSubcategoryId, isTranslationMode]
    );

    const scrollSelectedItemIntoView = () => {
        let attemptsLeft = 5;

        const tryScroll = () => {
            const selectedIcon = pickerWrapperRef.current?.querySelector(".category-picker-wrapper-list .bi-check-lg");
            const selectedItem = selectedIcon instanceof HTMLElement ? selectedIcon.closest("li") : null;

            if (selectedItem instanceof HTMLElement) {
                selectedItem.scrollIntoView({
                    block: "nearest",
                });
                return;
            }

            attemptsLeft -= 1;
            if (attemptsLeft > 0) {
                window.setTimeout(tryScroll, 30);
            }
        };

        window.setTimeout(tryScroll, 0);
    };

    const moveCategoryHandler = async () => {
        if (!canSubmitMove) return;

        if (selectedCategoryId === categoryToMove.id || selectedCategoryId === categoryToMove.parentCategory?.id) {
            handleClose();
            return;
        }

        await updateMenuCategory({
            variables: {
                id: categoryToMove.id,
                branchId: Number(branchId),
                input: {
                    name: primaryCategoryName,
                    parentCategoryId: selectedCategoryId,
                    faviconId: categoryToMove.favicon?.id ?? null,
                },
                companyId: +currentCompanyId,
                locale: primaryLanguageIso,
            },
            onCompleted: () => {
                toggleShowToast(t("Moved"));
            },
        });

        onMove();
        handleClose();
    };

    const applyCategorySelection = (currentCategory: CategoryPickerCategory | null) => {
        if (currentCategory?.isPicked) {
            setSelectedCategoryId(currentCategory.id);
            setCurrentCategoryId(currentCategory.id);
            setCurrentSubcategoryId(null);
            setSearchValue("");
            scrollSelectedItemIntoView();
        } else {
            setSelectedCategoryId(null);
            setCurrentCategoryId(null);
            setCurrentSubcategoryId(null);
        }
    };

    const applySubcategorySelection = (
        subCategory: CategoryPickerSubcategory,
        parentCategory: CategoryPickerCategory
    ) => {
        if (subCategory.isPicked) {
            setSelectedCategoryId(parentCategory.id);
            setCurrentCategoryId(parentCategory.id);
            setCurrentSubcategoryId(subCategory.id);
            setSearchValue("");
            scrollSelectedItemIntoView();
        } else {
            setSelectedCategoryId(null);
            setCurrentSubcategoryId(null);
            setCurrentCategoryId(null);
        }
    };

    const pickFirstSearchMatch = () => {
        const normalizedSearch = searchValue.trim().toLocaleLowerCase();
        if (!normalizedSearch) {
            return;
        }

        for (const category of pickerCategories) {
            if (category.name.toLocaleLowerCase().includes(normalizedSearch)) {
                applyCategorySelection({ ...category, isPicked: true });
                return;
            }

            const matchedSubcategory = category.subcategoryList.find((subcategory) =>
                subcategory.name.toLocaleLowerCase().includes(normalizedSearch)
            );
            if (matchedSubcategory) {
                applySubcategorySelection({ ...matchedSubcategory, isPicked: true }, category);
                return;
            }
        }
    };

    useEffect(() => {
        scrollSelectedItemIntoView();
    }, [pickerCategories, currentCategoryId, currentSubcategoryId]);

    return (
        <Modal centered show={show} onHide={handleClose}>
            <Modal.Header closeButton className="border-bottom-0">
                <Modal.Title className="fs-5">
                    {t("moveCategoryWithName", { categoryName: nameVm.displayText })}
                    {nameVm.isMissingTranslation ? (
                        <TranslationMissingDot tooltipText={t("Edit the language version")} />
                    ) : null}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body
                onKeyDownCapture={(event) => {
                    if (event.key !== "Enter") {
                        return;
                    }

                    if (!(event.target instanceof HTMLInputElement)) {
                        return;
                    }

                    event.preventDefault();
                    pickFirstSearchMatch();
                }}
            >
                {categoriesLoading ? (
                    <div className="d-flex justify-content-center py-4">
                        <Spinner heightContent={true} />
                    </div>
                ) : (
                    <div ref={pickerWrapperRef}>
                        <CategoryPickerComponent
                            serviceCategories={pickerCategories}
                            showMissingTranslationDot={isTranslationMode}
                            renderMissingTranslationDot={() => (
                                <TranslationMissingDot tooltipText={t("Edit the language version")} />
                            )}
                            onSearchChange={setSearchValue}
                            onChangeCategory={applyCategorySelection}
                            onChangeSubcategory={applySubcategorySelection}
                            isOpen={true}
                            i18n={i18next}
                        />
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="justify-content-between border-top-0">
                <Button
                    variant="primary"
                    className="custom-btn fw-bold"
                    type="submit"
                    disabled={!canSubmitMove}
                    onClick={moveCategoryHandler}
                >
                    {t("Move")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MoveCategoryModal;
